/**
 * veercal.oncosts.js
 * ─────────────────────────────────────────────────────────────────────────────
 * VEERCAL — Single source of truth for every government-set rate the
 * calculators rely on: ATO income tax brackets, Medicare levy, FBT (rate and
 * gross-up factors), Luxury Car Tax, ATO statutory residuals, state stamp
 * duty schedules, and EV concessions.
 *
 * IMPORTED BY (React): App.js · QuickCompare.jsx
 *
 * MIRRORED INLINE (static HTML, can't import — keep in sync at every update):
 *   public/novated.html      (FBT, tax brackets, Medicare, residuals, stamp duty)
 *   public/novated-lease-quote-checker.html  (window.VEERCAL_NOVATED_RATES:
 *                            tax brackets, Medicare levy, residual minimums)
 *
 * NOT MIRRORED: public/stamp-duty.html runs on the production engine
 *   (public/engines/core/, rates from src/core/rates.json) since overhaul
 *   Phase 7, stage 5, so it holds no copy of this file.
 *
 * HOW TO UPDATE (every 1 July):
 *   1. Check ato.gov.au for: tax brackets, Medicare phase-in, FBT rate &
 *      gross-up factors, LCT thresholds, statutory residual percentages.
 *   2. Check each state revenue office for stamp duty rates + EV concessions.
 *   3. Update the values below.
 *   4. Update the inline mirror block in novated.html
 *      (grep for RATES = { to find it).
 *   5. Update ONCOSTS_META.lastReviewed and nextReviewDue.
 *   6. Commit: "Rates update FY[xx]-[xx] — verified [date]"
 *
 * SOURCES (verified 15 October 2026):
 *   Income tax:   ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents
 *   Medicare:     ato.gov.au/.../medicare-levy-reduction-for-low-income-earners
 *   FBT:          ato.gov.au/tax-rates-and-codes/fringe-benefits-tax-rates-and-thresholds
 *   LCT:          ato.gov.au/tax-rates-and-codes/luxury-car-tax-rate-and-thresholds
 *   Car limit:    ato.gov.au/.../small-business-newsroom/car-thresholds-from-1-july
 *   Residuals:    ato.gov.au/businesses/leasing/
 *   VIC:          sro.vic.gov.au/rates-taxes-duties-and-levies/motor-vehicle-duty-current-rates
 *   NSW:          revenue.nsw.gov.au/taxes-duties-levies-royalties/motor-vehicle-duty
 *   QLD:          qld.gov.au/transport/registration/fees/duty/rates
 *   WA:           wa.gov.au/organisation/department-of-treasury-and-finance/vehicle-licence-duty
 *   SA:           revenuesa.sa.gov.au/stamp-duty-vehicles/rates
 *   TAS:          sro.tas.gov.au/motor-vehicle-duty/rates-of-duty
 *   ACT:          revenue.act.gov.au/duties/motor-vehicle-duty
 *   NT:           nt.gov.au/driving/rego/getting-an-nt-registration/get-electric-vehicle-registration-and-stamp-duty-concessions
 *
 * © 2025 Veercal Pty. Ltd. All rights reserved.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* ─── Review metadata ───────────────────────────────────────────────────────
   Update on every rates review. nextReviewDue is the *deadline* (next ATO
   indexation effective date) — when you do the update, advance both dates
   and bump financialYear. The UI surfaces these strings to users and tests
   pin them via `expect(ONCOSTS_META.lastReviewed)`. */
export const ONCOSTS_META = {
  lastReviewed:  "October 2026",
  /* Earlier than 1 July this cycle: new ACT duty rates start 1 Feb 2027, and
     the Budget EV FBT changes (if legislated) start 1 April 2027. */
  nextReviewDue: "1 February 2027",
  financialYear: "FY2026-27",
  source:        "ato.gov.au / state revenue offices",
};

/* ─── Shared scenario URL schema version (O16) ─────────────────────────────
   Bumped when the encoded scenario JSON shape changes in a breaking way
   (e.g. a field renamed or removed). Encoders stamp this onto the encoded
   object as `_v`; decoders read it and warn the user when the version
   doesn't match — links survive but the user sees a notice. */
export const SCENARIO_SCHEMA_VERSION = 1;

/* ─── Rate / methodology change log (O15) ──────────────────────────────────
   Each entry is one observable change a user might want to know about.
   Append at the top when something changes; the UI surfaces the newest entry
   as a small banner and links to the full list. Keep entries short and
   user-facing (not implementation detail).

   Schema: { date: "YYYY-MM-DD", title, body, surfaces?: ["calculator", "novated", ...] }
   surfaces controls which pages show the banner (omit = all). */
export const CHANGES = [
  {
    date: "2026-10-15",
    title: "FY2026–27 rates applied",
    body: "Income tax now uses the 2026–27 scale (15% lower rate) — earlier figures overstated tax at the 19%/32.5% rates. LCT thresholds rise to $91,661 (fuel-efficient) and $80,809 (other), Medicare low-income thresholds move to $28,011/$35,013, and the ATO car limit is $69,883. Stamp duty: NSW's EV exemption (ended Dec 2023) and the ACT zero-emission exemption (ended Sept 2025) are removed, ACT uses its Sept 2025 rates, QLD EVs/PHEVs use the $2/$100 hybrid rate, and NT's first-$50,000 EV concession is applied.",
  },
  {
    date: "2026-04-15",
    title: "FY2025–26 rates verified",
    body: "Tax brackets, FBT, Medicare, LCT thresholds, and state stamp duty schedules all reviewed against ATO and state revenue offices. Novated lease methodology now correctly applies the GST input credit on packaged running costs and uses ATO 'cost price' (base + LCT) for FBT and residual calculations.",
  },
  {
    date: "2026-03-24",
    title: "EU-AU trade deal: LCT-FE threshold proposed at $120,000",
    body: "The EU-Australia free trade agreement (signed 24 Mar 2026) proposes raising the fuel-efficient LCT threshold from $91,387 to $120,000. Not yet legislated — when it passes, EV FBT exemption eligibility will expand significantly. Watch this space.",
    surfaces: ["calculator", "novated", "stamp-duty"],
  },
  {
    date: "2026-04-01",
    title: "PHEV FBT exemption — grandfathering window",
    body: "PHEV exemption for novated leases ended 1 April 2025 for new agreements. Existing PHEV novated leases in use before that date stay FBT-exempt until the lease ends, unless it's changed or extended. The calculator models new arrangements only.",
    surfaces: ["calculator", "novated"],
  },
];

/* ─── ATO income tax brackets (resident, FY2026-27) ───────────────────────
   Stage-3 thresholds (from 1 July 2024) with the lowest rate cut from 16% to
   15% on 1 July 2026 (legislated; falls to 14% on 1 July 2027). Each bracket:
   marginal rate within band + base tax for incomes that have fully crossed
   the bracket's lower edge. The mtr() helpers in App.js / QuickCompare derive
   a marginal rate from this same array — keep in sync.
─────────────────────────────────────────────────────────────────────────── */
export const TAX_BRACKETS = [
  { min: 0,      max: 18200,      rate: 0,     base: 0,     label: "Nil"   },
  { min: 18201,  max: 45000,      rate: 0.15,  base: 0,     label: "15%"   },
  { min: 45001,  max: 135000,     rate: 0.30,  base: 4020,  label: "30%"   },
  { min: 135001, max: 190000,     rate: 0.37,  base: 31020, label: "37%"   },
  { min: 190001, max: Infinity,   rate: 0.45,  base: 51370, label: "45%"   },
];

/**
 * incomeTax — compute total income tax payable for a given gross salary
 * using TAX_BRACKETS above (no Medicare, no offsets).
 */
export function incomeTax(salary) {
  const b = TAX_BRACKETS.slice().reverse().find(b => salary > b.min);
  return b ? b.base + (salary - b.min) * b.rate : 0;
}

/* ─── Medicare levy (singles thresholds, latest published: 2025-26) ────────
   2% above MEDICARE_PHASE_TOP. Between the lower and upper thresholds, the
   levy phases in at 10c per $1 of income above MEDICARE_THRESHOLD, capped
   at the full 2% rate. Below the lower threshold: no levy.
   The low-income thresholds for a year are only set in the following May
   Budget, so the 2025-26 figures are the latest available during 2026-27.
─────────────────────────────────────────────────────────────────────────── */
export const MEDICARE_LEVY        = 0.02;
export const MEDICARE_THRESHOLD   = 28011;   /* phase-in lower bound */
export const MEDICARE_PHASE_TOP   = 35013;   /* phase-in upper bound */

/**
 * medicareLevy — Medicare levy payable for a given salary, with phase-in band.
 * Returns a dollar amount. Below the lower threshold returns 0; above the
 * upper threshold returns the full 2% rate; in between, 10c per $1 above
 * the lower threshold (which equals 2% of income at the upper threshold).
 */
export function medicareLevy(salary) {
  if (salary <= MEDICARE_THRESHOLD) return 0;
  if (salary >= MEDICARE_PHASE_TOP) return salary * MEDICARE_LEVY;
  return (salary - MEDICARE_THRESHOLD) * 0.10;
}

/* ─── Fringe Benefits Tax (FBT year ending 31 March 2027) ──────────────────
   FBT year runs 1 April to 31 March. Type 1 gross-up applies where the
   employer can claim GST input tax credits (the standard case for novated
   leases). Type 2 applies where they can't.
─────────────────────────────────────────────────────────────────────────── */
export const FBT_RATE             = 0.47;     /* employer FBT rate           */
export const FBT_GROSSUP_T1       = 2.0802;   /* GST-creditable benefits     */
export const FBT_GROSSUP_T2       = 1.8868;   /* non-GST-creditable benefits */
export const FBT_STATUTORY        = 0.20;     /* statutory % for car benefit */

/* ─── GST input credit on the vehicle purchase (FY2026-27) ─────────────────
   Under a novated lease the financier acquires the car and claims the GST
   input tax credit on the purchase, so the lease is funded on the GST-
   EXCLUSIVE price. The credit a business can claim is capped at 1/11 of the
   ATO car limit. The car limit is the same for EV and ICE (it is the income-
   tax depreciation / GST limit, NOT the LCT threshold).
   ATO: "Purchasing a motor vehicle" / "Changes to car thresholds from 1 July".
─────────────────────────────────────────────────────────────────────────── */
export const GST_CAR_LIMIT          = 69883;   /* FY2026-27 ATO car limit     */
export const GST_PURCHASE_CREDIT_CAP = 6353;   /* = round(69883 / 11) max claim */

/* ─── ATO statutory residual minimums (by annual km band) ──────────────────
   These percentages are widely used by Australian novated-lease providers
   as the residual on a 36-month lease, adjusted by annual km. The strict
   ATO IT 28 minimums vary by lease *term* (65.63% / 56.25% / 46.88% /
   37.5% / 28.13% for 1/2/3/4/5 yr) — H2 in the audit flags that mismatch.
   For now this table is the industry-convention defaults the calculators
   suggest; users can override.
─────────────────────────────────────────────────────────────────────────── */
export const RESIDUAL_BY_KM = {
  15000:  0.5288,
  25000:  0.4669,
  35000:  0.4050,
  45000:  0.3431,
  over45: 0.2812,
};

/**
 * getResidualByKm — return the suggested residual % for a given annual km,
 * assuming a 36-month lease (the industry-default term the RESIDUAL_BY_KM
 * table was calibrated to). For other terms use getResidualByTermAndKm.
 */
export function getResidualByKm(annualKm) {
  if (annualKm <= 15000) return RESIDUAL_BY_KM[15000];
  if (annualKm <= 25000) return RESIDUAL_BY_KM[25000];
  if (annualKm <= 35000) return RESIDUAL_BY_KM[35000];
  if (annualKm <= 45000) return RESIDUAL_BY_KM[45000];
  return RESIDUAL_BY_KM.over45;
}

/* ─── ATO IT 28 minimum residuals (by lease term) ──────────────────────────
   These are the strict ATO minimums — the residual at which the lease is
   still treated as a finance lease (vs operating lease) for tax purposes.
   Veercal scales the 36-month km table above by the ratio of these terms
   to 36 months, so a 5-year lease at low km still recognises the longer
   depreciation period while preserving the km-based usage adjustment.
─────────────────────────────────────────────────────────────────────────── */
export const RESIDUAL_BY_TERM = {
  1: 0.6563,
  2: 0.5625,
  3: 0.4688,   /* anchor — matches mid-band km value */
  4: 0.3750,
  5: 0.2813,
};

/**
 * getResidualByTermAndKm — combined term + km lookup.
 *
 * The RESIDUAL_BY_KM table is calibrated to a 36-month lease (3-year industry
 * default). To support other terms, scale by RESIDUAL_BY_TERM[term] / 0.4688
 * — preserves the km-based usage adjustment AND respects the term-based
 * ATO IT 28 minimum.
 *
 * Examples (15,000 km/yr): 1yr 74.0% · 2yr 63.5% · 3yr 52.88% · 4yr 42.3% · 5yr 31.7%
 *
 * @param {number} termYears  Lease term in years (1-5, clamped). Non-integer
 *                            years are rounded to the nearest band.
 * @param {number} annualKm   Annual km estimate (used by getResidualByKm).
 */
export function getResidualByTermAndKm(termYears, annualKm) {
  const term = Math.max(1, Math.min(5, Math.round(termYears)));
  const termFactor = RESIDUAL_BY_TERM[term] / RESIDUAL_BY_TERM[3];
  return getResidualByKm(annualKm) * termFactor;
}

/* ─── LCT thresholds (ATO, FY2026-27) ────────────────────────────────────── */
export const LCT_THRESHOLD_STD = 80809;   /* Petrol / diesel / hybrid        */
export const LCT_THRESHOLD_FE  = 91661;   /* Fuel-efficient: EV, PHEV        */
/* NOTE: EU-AU FTA (signed 24 Mar 2026) proposes a new $120,000 threshold for
   zero-emission vehicles. Not in the ATO's 2026-27 table — NOT YET LAW.
   Update once implementing legislation passes.                              */
export const LCT_THRESHOLD_FE_PROPOSED = 120000;
export const LCT_RATE = 0.33;

/**
 * calcLCT — Luxury Car Tax payable
 * @param {number} vehiclePrice  Purchase price including GST
 * @param {boolean} isFuelEfficient  true for EV / PHEV. From 1 July 2025 the
 *                                   fuel-efficient test is ≤3.5 L/100km, so
 *                                   most non-plug-in hybrids no longer qualify.
 * @returns {number}  LCT payable (0 if under threshold)
 */
export function calcLCT(vehiclePrice, isFuelEfficient) {
  const threshold = isFuelEfficient ? LCT_THRESHOLD_FE : LCT_THRESHOLD_STD;
  if (vehiclePrice <= threshold) return 0;
  return ((vehiclePrice - threshold) / 1.1) * LCT_RATE;
}

/* ─── EV FBT exemption ────────────────────────────────────────────────────── */
export const EV_FBT_EXEMPT_THRESHOLD = LCT_THRESHOLD_FE; /* $91,661 FY2026-27 */
/* 2026-27 Budget (announced 5 May 2026, NOT YET LAW per ATO): full exemption
   continues to 31 March 2027. From 1 April 2027 new leases on EVs over $75,000
   get a 25% discount only (15% statutory rate); from 1 April 2029 all EVs get
   the 25% discount. Existing leases unaffected. Revisit before 1 April 2027. */

/**
 * isEvFbtExempt — does a car qualify for the BEV FBT exemption under a novated
 * lease, based on its drive-away price?
 *
 * @param {number} driveAway     Drive-away price (as user enters it).
 * @param {string} vehicleType   'ev' (BEV), 'phev', 'new', 'used'.
 *                               Use the same vType you would pass to calcSD.
 * @param {string} state         Two-letter state code — used by the
 *                               drive-away → base extraction.
 * @returns {boolean}            True iff the car qualifies for the exemption.
 *
 * ATO rule (s.8A FBTAA 1986, EV Exemption Act 2022): a car is exempt from FBT
 * if it is a zero / low emissions vehicle first held and used on or after
 * 1 July 2022, AND the value of the car at first retail sale was below the LCT
 * threshold for fuel-efficient vehicles, AND no LCT has been payable on any
 * sale or import. Equivalently: if LCT would have been payable, NOT exempt.
 *
 * PHEV exemption ended for new agreements 1 April 2025. Existing PHEV leases
 * in use before that date stay exempt until the lease ends (unless the binding
 * commitment changes — ATO "FBT on plug-in hybrid electric vehicles"), but
 * Veercal models new arrangements only — so PHEV returns false here.
 *
 * Implementation: extract the base price from drive-away using the standard
 * SD + LCT solver, then check whether LCT would have been payable on that
 * base. Matches the methodology used in every Veercal calculator so the answer
 * is the same on /calculator, /compare, and /novated for the same scenario.
 *
 * Near the threshold the test depends on dealer delivery and other on-costs
 * that aren't isolated from drive-away. For borderline cars (extracted base
 * within ~$2k of the threshold), confirm the dealer's invoice value directly.
 */
export function isEvFbtExempt(driveAway, vehicleType, state) {
  /* Only BEV qualifies for new agreements. PHEV grandfathered; ICE never. */
  if (vehicleType !== 'ev') return false;
  /* Extract base price using the standard drive-away → base solver, then
     test whether LCT would have been payable on it. */
  let base = driveAway * 0.88;
  for (let i = 0; i < 20; i++) {
    const next = driveAway - calcLCT(base, true) - calcSD(base + calcLCT(base, true), state, 'ev');
    if (Math.abs(next - base) < 0.5) { base = next; break; }
    base = next;
  }
  return calcLCT(base, true) === 0;
}

/* ─── Stamp duty (motor vehicle duty) by state ────────────────────────────── */
/*
 * All rates are for new or used PASSENGER cars unless noted.
 * Commercial vehicles (utes, vans): VIC and SA have different rates — see
 *   calcSD() which accepts an optional isCommercial flag.
 *
 * VIC: Duties Act 2000 s.218 — $X per $200, value rounded UP to nearest $200
 *   ≤ $80,809:   $8.40/$200  = 4.2%   (band edge = LCT other-vehicle threshold)
 *   $80,809.01–100k: $10.40/$200 = 5.2%
 *   $100,001–150k: $14.00/$200 = 7%
 *   > $150,000:  $18.00/$200  = 9%
 *   Green/EV (≤120g CO₂/km):  $8.40/$200 flat (any price)
 *   Non-passenger new (utes/vans): $5.40/$200 = 2.7%
 *
 * NSW: Revenue NSW — 3% under $45k; $1,350 + 5% above $45k
 * QLD: Duties Act 2001 — cylinder-based. Simplified as standard 4-cyl (3%/5%);
 *      hybrid/EV/PHEV $2/$100 up to $100k, $4/$100 above
 * WA: 2.75% up to $25k; sliding to 6.5% at $50k; flat 6.5% above $50k
 * SA: $60 + $4 per $100 over $3,000 (non-commercial); $30 + $3 per $100 (commercial)
 * TAS: $3/$100 up to $35k; step at $35-40k; 4% above $40k
 * ACT: Emissions-based, rates from 1 Sep 2025 to 31 Jan 2027 (new rates from
 *      1 Feb 2027). Cat. C (176–220 g/km, standard petrol; also all used
 *      non-ZEVs): $3.17/$100 under $45k; $1,426.50 + $5.62/$100 to $80k;
 *      $3,393.50 + $8/$100 above $80k.
 *      Zero-emission (Cat. AAA): $2.50/$100 under $45k; $1,125 + $4/$100 to
 *      $80k; $2,525 + $8/$100 above $80k. (Full ZEV exemption ended 31 Aug 2025.)
 * NT:  Flat 3%. BEV/PHEV: first $50,000 exempt until 30 Jun 2027.
 */
const SD_PASSENGER = {
  VIC: (p) => {
    const v = Math.ceil(p / 200) * 200;
    if (p <= 80809)  return (v / 200) * 8.40;
    if (p <= 100000) return (v / 200) * 10.40;
    if (p <= 150000) return (v / 200) * 14.00;
    return (v / 200) * 18.00;
  },
  NSW: (p) => p < 45000 ? p * 0.03 : 1350 + (p - 45000) * 0.05,
  QLD: (p) => p <= 100000 ? p * 0.03 : p * 0.05,
  WA:  (p) => {
    if (p <= 25000) return p * 0.0275;
    if (p <= 50000) { const R = 2.75 + (p - 25000) / 6666.66; return p * R / 100; }
    return p * 0.065;
  },
  SA:  (p) => p <= 1000 ? Math.max(5, Math.ceil(p / 100))
            : p <= 2000 ? 10 + (p - 1000) * 0.02
            : p <= 3000 ? 30 + (p - 2000) * 0.03
            : 60 + (p - 3000) * 0.04,
  TAS: (p) => {
    if (p <= 600)   return 20;
    if (p <= 35000) return Math.ceil(p / 100) * 3;
    if (p <= 40000) return 1050 + Math.ceil((p - 35000) / 100) * 11;
    return p * 0.04;
  },
  ACT: (p) => p < 45000 ? p * 0.0317 : p <= 80000 ? 1426.50 + (p - 45000) * 0.0562 : 3393.50 + (p - 80000) * 0.08,
  NT:  (p) => p * 0.03,
};

/* VIC green/EV flat rate — $8.40/$200 regardless of price */
const SD_VIC_GREEN = (p) => (Math.ceil(p / 200) * 200 / 200) * 8.40;

/* QLD hybrid/electric rate — $2/$100 up to $100k, $4/$100 above (whole value) */
const SD_QLD_HYBRID = (p) => p <= 100000 ? p * 0.02 : p * 0.04;

/* ACT zero-emission (Cat. AAA) rate */
const SD_ACT_ZEV = (p) => p < 45000 ? p * 0.025 : p <= 80000 ? 1125 + (p - 45000) * 0.04 : 2525 + (p - 80000) * 0.08;

/* Commercial vehicle rates (VIC and SA only) */
const SD_COMMERCIAL = {
  VIC: (p) => (Math.ceil(p / 200) * 200 / 200) * 5.40, /* $5.40/$200 = 2.7% */
  SA:  (p) => p <= 1000 ? Math.max(5, Math.ceil(p / 100))
            : p <= 2000 ? 10 + (p - 1000) * 0.02
            : 30 + (p - 2000) * 0.03,                   /* lower rate than non-commercial */
};

/* ─── EV stamp duty concessions by state ──────────────────────────────────── */
/* Verified 15 October 2026 — these change regularly, always verify before July 1.
   'exempt' (duty-free up to threshold) is still supported by calcSD but no
   state currently offers one: NSW's ended 31 Dec 2023, ACT's 31 Aug 2025.  */
const EV_SD_CONCESSIONS = {
  NSW: { type: 'none',   note: 'Exemption ended 31 Dec 2023; standard rates apply' },
  ACT: { type: 'lower',  note: 'Zero-emission rate ($2.50/$100 under $45k) applies; full exemption ended Aug 2025' },
  NT:  { type: 'firstExempt', amount: 50000, note: 'First $50,000 exempt (up to $1,500) for BEVs/PHEVs until Jun 2027' },
  VIC: { type: 'flat',   note: 'Green car flat rate $8.40/$200 regardless of price' },
  QLD: { type: 'lower',  note: 'Hybrid/electric rate $2/$100 (up to $100k) applies' },
  WA:  { type: 'none',   note: 'Standard rates apply' },
  SA:  { type: 'none',   note: 'Standard rates apply' },
  TAS: { type: 'none',   note: 'Exemption ended June 2023; standard rates apply' },
};

/**
 * calcSD — Calculate motor vehicle stamp duty
 *
 * @param {number}  dutiableValue  Vehicle price + dealer delivery + LCT (if applicable)
 * @param {string}  state          State code: 'VIC' | 'NSW' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT'
 * @param {string}  vehicleType    'new' | 'used' | 'ev' | 'phev' — affects EV concessions and VIC green rate
 * @param {boolean} isCommercial   true for utes, vans, commercial goods vehicles (affects VIC and SA)
 * @returns {number}  Stamp duty payable (rounded to nearest dollar by caller)
 */
export function calcSD(dutiableValue, state, vehicleType = 'new', isCommercial = false) {
  const isEV   = vehicleType === 'ev';
  const isGreen = vehicleType === 'ev' || vehicleType === 'phev'; /* VIC green, QLD hybrid, NT plug-in */

  /* 1. Check EV exemptions first */
  if (isEV) {
    const conc = EV_SD_CONCESSIONS[state];
    if (conc && conc.type === 'exempt' && dutiableValue <= conc.threshold) return 0;
  }

  /* 2. Commercial vehicle rates (VIC and SA) */
  if (isCommercial && SD_COMMERCIAL[state]) {
    return SD_COMMERCIAL[state](dutiableValue);
  }

  /* 3. VIC green/EV flat rate */
  if (state === 'VIC' && isGreen) {
    return SD_VIC_GREEN(dutiableValue);
  }

  /* 4. QLD hybrid/electric rate (BEV and PHEV) */
  if (state === 'QLD' && isGreen) {
    return SD_QLD_HYBRID(dutiableValue);
  }

  /* 5. ACT zero-emission (Cat. AAA) rate — BEV only; PHEV stays on Cat. C */
  if (state === 'ACT' && isEV) {
    return SD_ACT_ZEV(dutiableValue);
  }

  /* 6. NT plug-in concession — first $50k of value exempt (BEV and PHEV) */
  if (state === 'NT' && isGreen) {
    const conc = EV_SD_CONCESSIONS.NT;
    return SD_PASSENGER.NT(Math.max(0, dutiableValue - conc.amount));
  }

  /* 7. Standard passenger rate */
  const fn = SD_PASSENGER[state] || SD_PASSENGER.VIC;
  return fn(dutiableValue);
}

/**
 * getEvConcession — Returns the EV concession descriptor for a state
 * Useful for displaying explanatory text in the UI.
 */
export function getEvConcession(state) {
  return EV_SD_CONCESSIONS[state] || { type: 'none', note: 'Standard rates apply' };
}

/**
 * calcAllStates — Calculate stamp duty for all states at once.
 * Useful for comparison tables.
 * @param {number} dutiableValue
 * @param {string} vehicleType
 * @returns {Object}  { VIC: number, NSW: number, ... }
 */
export function calcAllStates(dutiableValue, vehicleType = 'new') {
  const states = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
  return Object.fromEntries(
    states.map(s => [s, calcSD(dutiableValue, s, vehicleType)])
  );
}

/* ─── Rate check benchmark (approved by Pat, 20 Oct 2026) ────────────────────
   The benchmark B for the "rate check" meter (docs/brand/DESIGN.md §5,
   public/design/v2/meter-rules.js). Not a government rate, but kept here so
   every calculator reads one number.
   Source: RBA Statistical Table F8, series FLRPFNFTF, "Lending rates; Personal
   credit; New loans funded in the month; Fixed-term loans; Fixed-rate".
   https://www.rba.gov.au/statistics/tables/xls/f08hist.xlsx
   Value: the average of the latest 6 monthly values, to one decimal place.
   Mar–Aug 2026: 10.33, 10.55, 10.42, 9.97, 10.48, 10.44 → 10.365 → 10.4
   (F8 published 8 Oct 2026).
   Refresh QUARTERLY (January, April, July, October), not just on 1 July.
   Mirrored in public/end-of-term.html, public/landing.html and
   public/car-loan-quote-checker.html (window.VEERCAL_RATE_BENCHMARK);
   src/veercal.rates-sync.test.js checks the mirrors. */
export const RATE_BENCHMARK = {
  pct:     10.4,
  period:  'March to August 2026',
  series:  'RBA table F8, FLRPFNFTF (new fixed-rate personal fixed-term loans)',
  source:  'https://www.rba.gov.au/statistics/tables/xls/f08hist.xlsx',
  nextRefresh: 'January 2027',
};

/* ─── UPDATE CHECKLIST (run each 1 July) ─────────────────────────────────────
   Quarterly, separately (January, April, July, October):
   [ ] RATE_BENCHMARK — download RBA F8, average the latest 6 months of
       FLRPFNFTF to one decimal place, update pct, period and nextRefresh, and
       mirror pct and period into public/end-of-term.html, public/landing.html
       and public/car-loan-quote-checker.html

   ATO (federal):
   [ ] TAX_BRACKETS — verify thresholds, marginal rates, and pre-computed
       base amounts against ato.gov.au/individuals
   [ ] MEDICARE_LEVY, MEDICARE_THRESHOLD, MEDICARE_PHASE_TOP
   [ ] FBT_RATE, FBT_GROSSUP_T1, FBT_GROSSUP_T2, FBT_STATUTORY
   [ ] GST_CAR_LIMIT (ATO car limit) and GST_PURCHASE_CREDIT_CAP (= round/11)
   [ ] LCT_THRESHOLD_STD and LCT_THRESHOLD_FE
   [ ] Check if EU-AU FTA legislated: add the $120,000 ZEV threshold if so
   [ ] Check if the 2026-27 Budget EV FBT changes ($75k cap from 1 Apr 2027)
       are law — isEvFbtExempt() needs a lease-start-date / $75k test if so
   [ ] RESIDUAL_BY_KM — only changes if ATO IT 28 / TR 95/26 schedule changes

   State (each revenue office):
   [ ] VIC SRO: verify $8.40/$10.40/$14/$18 per $200 bands; first band edge
       tracks LCT_THRESHOLD_STD. Green-car concession above the LCT threshold
       ends 1 Jul 2027 (announced in VIC 2026-27 Budget)
   [ ] Revenue NSW: verify $45k threshold and 3%/5% rates
   [ ] QLD Transport: cylinder-based rates + hybrid/EV $2/$4 per $100
   [ ] WA OSR: verify 2.75%/sliding/6.5% structure
   [ ] Revenue SA: verify non-commercial $60+4% and commercial $30+3%
   [ ] SRO TAS: verify $3/$100 and $40k threshold
   [ ] ACT Revenue: verify Cat. C and AAA rates (new rates from 1 Feb 2027)
   [ ] NT: verify 3% flat rate and first-$50k BEV/PHEV concession (ends 30 Jun 2027)
   [ ] EV_SD_CONCESSIONS — add/remove any new state EV programs

   Bookkeeping:
   [ ] Update ONCOSTS_META.lastReviewed and nextReviewDue
   [ ] Mirror every change above into the inline copies in:
         public/novated.html      → RATES block at the top of the inline <script>
         public/novated-lease-quote-checker.html → window.VEERCAL_NOVATED_RATES
           (tax brackets, Medicare levy, residual minimums)
       (static HTML can't import, so these must be hand-synced)
   [ ] Homepage example card — if a rates update changes any default result,
       `src/homepage-example.test.js` fails: update the card's figures in
       `public/landing.html` (Pat approves the wording).
   [ ] The novated RATES block sits inside its pinned calculation script, so
       re-pin it with Pat's approval:
         node scripts/golden/update-engines.js --confirm "Rates update FY[xx]-[xx]"
   [ ] Commit: "Rates update FY[xx]-[xx] — verified [date]"
── */
